'use client'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { useTranslations } from 'use-intl'
import { downloadAuthenticatedFile, DownloadError, formatBytes } from '@/lib/ai/download'
import type { ChatAttachment, FileAttachmentData } from '@/lib/ai/types'

/**
 * "file" attachment — a generated PDF. Download goes through an authenticated
 * fetch (bearer token) → blob → <a download>, never a plain href.
 */
export function FileCard({ attachment }: { attachment: ChatAttachment }) {
  const t = useTranslations('aiPanel.fileCard')
  const data = (attachment.data ?? {}) as FileAttachmentData
  const [busy, setBusy] = useState(false)
  const filename = data.filename || attachment.url?.split('/').pop() || 'report.pdf'
  const size = formatBytes(data.size_bytes)

  // Server text stays as sent; with none (empty statusText over HTTP/2) the frontend words the status itself.
  const describeFailure = (err: unknown): string => {
    if (err instanceof DownloadError) return err.message || t('downloadFailedStatus', { status: String(err.status) })
    return err instanceof Error ? err.message : t('tryAgain')
  }

  const handleDownload = async () => {
    if (!attachment.url || busy) return
    setBusy(true)
    try {
      await downloadAuthenticatedFile(attachment.url, filename)
      toast.success(t('downloadStarted'), { description: filename })
    } catch (err) {
      if (err instanceof DownloadError && err.status === 401) {
        toast.error(t('sessionExpired'), { description: t('sessionExpiredDescription') })
      } else if (err instanceof DownloadError && err.status === 404) {
        toast.error(t('fileNotFound'), { description: t('fileNotFoundDescription') })
      } else {
        toast.error(t('downloadFailed'), { description: describeFailure(err) })
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={cn('flex items-center gap-3 rounded-xl border border-border/50 bg-white px-3 py-2.5 shadow-soft')}>
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
        <Icons.fileText className="h-4 w-4" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-brand-navy">{attachment.title || filename}</p>
        <p className="truncate text-xs text-muted-foreground">
          {[filename, size, data.report_type ? data.report_type.replace(/_/g, ' ') : null].filter(Boolean).join(' · ')}
        </p>
        {attachment.description && <p className="mt-0.5 text-xs text-muted-foreground">{attachment.description}</p>}
      </div>
      <Button size="sm" variant="outline" onClick={handleDownload} disabled={busy || !attachment.url} className="flex-shrink-0 gap-1.5">
        {busy ? <Icons.loader className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <Icons.download className="h-3.5 w-3.5" strokeWidth={1.5} />}
        {busy ? t('fetching') : t('download')}
      </Button>
    </div>
  )
}
